import { useState } from "react";
import { MoonStar, Ticket, BellRing } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { EVENT_CONFIG, API } from "@/config/eventConfig";
import { Reveal, Eyebrow, Thermometer, CountUp } from "./shared";

const AfterParty = () => {
  const { afterParty: ap, urls, images, afterPartyMoments } = EVENT_CONFIG;
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const goAlerts = () => {
    if (urls.afterPartyTickets) {
      window.open(urls.afterPartyTickets, "_blank", "noopener,noreferrer");
      return;
    }
    document.getElementById("after-party-alerts")?.scrollIntoView({ behavior: "smooth", block: "center" });
    toast.info("After-party tickets drop soon — leave your email and be first in line.");
  };

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSending(true);
    try {
      await axios.post(`${API}/notify`, { email, source: "after-party" });
      toast.success("You're first in line — after-party details are coming to your inbox.");
      setEmail("");
    } catch {
      toast.error("Please enter a valid email address.");
    } finally {
      setSending(false);
    }
  };

  const gallery = [images.dancing, images.club, images.danceFloor, images.dj];

  return (
    <section id="after-party" data-testid="after-party-section" className="relative py-24 sm:py-32 bg-night grain overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,0,127,0.12),transparent_55%),radial-gradient(ellipse_at_bottom,rgba(0,229,255,0.1),transparent_55%)]" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center gap-14">
        <Reveal className="text-center flex flex-col items-center gap-5">
          <div className="flex items-center gap-4">
            <span className="font-serif italic text-3xl sm:text-4xl text-neon leading-none">09</span>
            <span className="h-px w-10 sm:w-16 bg-neon/40" />
            <Eyebrow className="!text-neon">The Official After Party</Eyebrow>
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ivory leading-[1.05]">
            THE GALA ENDS.
            <br />
            <span className="font-script text-magenta text-5xl sm:text-6xl lg:text-7xl normal-case">the party doesn't.</span>
          </h2>
          <p className="text-cream text-base sm:text-lg max-w-2xl leading-relaxed">
            Lose the tie. Keep the crown. We're keeping the celebration going — and raising another{" "}
            <span className="text-neon font-semibold">$250,000</span> for Styles 4 Kidz.
          </p>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs sm:text-sm uppercase tracking-[0.22em] text-cream/80">
            <span className="inline-flex items-center gap-2">
              <MoonStar size={14} className="text-magenta" /> {ap.venue}
            </span>
            <span>{ap.dateDisplay}</span>
            <span data-testid="after-party-time">
              {ap.startTime} – {ap.endTime}
              {ap.startTimeFlag && (
                <span className="ml-2 text-[10px] text-neon/70 normal-case tracking-normal">[{ap.startTimeFlag}]</span>
              )}
            </span>
          </div>
        </Reveal>

        <Reveal className="w-full max-w-3xl pb-8">
          <p className="text-center text-xs uppercase tracking-[0.35em] text-neon mb-6">
            The Second Goal: $250,000
          </p>
          <Thermometer
            goal={ap.goal}
            raised={ap.raised}
            accent="neon"
            testidPrefix="after-party-thermometer"
            milestones={[
              { value: 0, label: "$0" },
              { value: 62500, label: "$62.5K" },
              { value: 125000, label: "$125K" },
              { value: 187500, label: "$187.5K" },
              { value: 250000, label: "$250K" },
            ]}
          />
          <p className="mt-12 text-center text-sm text-cream/70">
            Raised so far:{" "}
            <span className="text-neon font-semibold" data-testid="after-party-raised">
              <CountUp value={ap.raised} prefix="$" />
            </span>
          </p>
        </Reveal>

        <Reveal className="w-full">
          <p className="text-xs uppercase tracking-[0.35em] text-magenta mb-5 text-center">From black tie to after dark</p>
          <div className="flex gap-4 sm:gap-5 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
            {gallery.map((src, gi) => (
              <div
                key={src}
                className={`group shrink-0 w-64 sm:w-80 snap-center overflow-hidden border border-neon/25 spotlight-card ${
                  gi % 2 === 0 ? "rounded-t-[9rem] rounded-b-2xl" : "rounded-2xl"
                }`}
              >
                <img
                  src={src}
                  alt="After party energy"
                  loading="lazy"
                  className="w-full h-72 sm:h-80 object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
                />
              </div>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap justify-center gap-2.5">
            {afterPartyMoments.map((m) => (
              <span key={m} className="px-4 py-2 rounded-full border border-magenta/30 text-ivory/85 text-[11px] uppercase tracking-[0.18em]">
                {m}
              </span>
            ))}
          </div>
        </Reveal>

        <Reveal className="flex flex-col sm:flex-row items-center gap-4">
          <button
            data-testid="after-party-tickets-button"
            onClick={goAlerts}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-magenta to-neon text-ink text-xs sm:text-sm uppercase tracking-[0.18em] font-bold hover:shadow-[0_16px_45px_-8px_rgba(255,0,127,0.6)] transition-shadow"
          >
            <Ticket size={15} /> Get After Party Tickets
          </button>
          <button
            data-testid="after-party-alerts-button"
            onClick={goAlerts}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-neon/50 text-neon text-xs sm:text-sm uppercase tracking-[0.18em] font-semibold hover:bg-neon/10 transition-colors"
          >
            <BellRing size={15} /> Get Drop Alerts
          </button>
        </Reveal>

        <Reveal className="w-full max-w-xl">
          <div
            id="after-party-alerts"
            data-testid="after-party-alerts-form"
            className="rounded-3xl border border-neon/30 bg-white/[0.03] p-7 sm:p-8 text-center"
          >
            <p className="font-script text-3xl text-magenta">be first in line</p>
            <p className="mt-2 text-cream/70 text-sm leading-relaxed">
              After party admission is ticketed separately from the gala — details drop soon.
              Leave your email for first access.
            </p>
            <form onSubmit={subscribe} className="mt-5 flex flex-col sm:flex-row gap-3">
              <input
                data-testid="after-party-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                className="flex-1 bg-night/70 border border-neon/25 rounded-xl px-5 py-3.5 text-ivory placeholder:text-cream/40 focus:outline-none focus:border-neon/60"
              />
              <button
                data-testid="after-party-notify-button"
                type="submit"
                disabled={sending}
                className="px-7 py-3.5 rounded-xl bg-gradient-to-r from-magenta to-neon text-ink text-xs uppercase tracking-[0.18em] font-bold disabled:opacity-60"
              >
                {sending ? "Joining…" : "Notify Me"}
              </button>
            </form>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default AfterParty;
